import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Col, Divider, Form, message, notification, Row } from 'antd'
import { Formik } from 'formik'
import { SelectValue } from 'antd/lib/select'
import { UploadChangeParam } from 'antd/lib/upload/interface'
import { useDispatch, useSelector } from 'react-redux'
import Compressor from 'compressorjs'
import moment from 'moment'

import {
  BudgetAllocationsCheck,
  BudgetChartBudgets,
  Gender,
  Id,
  Request,
  RequestTicket,
  Service
} from 'src/types'

import { AddImage, Modal } from 'src/components/Ant'
import { ApplicationState } from 'src/store'
import {
  DatePicker,
  Input,
  InputNumber,
  Select,
  SelectResource
} from 'src/components/Formik'
import {
  getBudgetAllocationsCheck,
  requestStandardSharingsReduce
} from 'src/views/Requests/views/Form/components/Steps/Assignment/components/Budget/budget-summary'
import {
  getInitialValues,
  getValidationSchema,
  ReceiptImageFileTypeAvailable
} from 'src/core/forms/receipt'
import { Option } from 'src/components/Formik/Select'
import {
  saveReceipt,
  saveReceiptFailure
} from 'src/store/ducks/receipts/actions'
import { useT, stringify } from 'src/core/hooks/use-translation'
import BudgetReceipt from 'src/views/Requests/views/Form/components/Steps/Assignment/components/Budget/budget-receipt'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'
import { isTenant334081949181 } from 'src/core/utils/tenant334081949181'
import { isTenant559042084949 } from 'src/core/utils/isTenant559042084949'
import * as CSS from './styles'
import ThumbnailPreview from '../ThumbnailPreview'
import InputCurrencyFreeDecimals from 'src/components/Formik/InputCurrencyFreeDecimals'

interface Props {
  receiptEdit?: RequestTicket
  request: Request
  toggle(): void
  visible: boolean
}

interface UserOption extends Option {
  gender?: Gender
}

function getBase64(file: File | Blob): Promise<string | ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

export default function ModalCreationEditing({
  receiptEdit,
  request,
  toggle,
  visible
}: Props): JSX.Element {
  const {
    me: { user },
    receipts,
    resources
  } = useSelector((state: ApplicationState) => ({
    me: state.me,
    receipts: state.receipts,
    resources: state.resources
  }))

  const numberSizeForUpload = 4
  const isTenant284264114255 = +user.tenant_id === 284264114255
  const uploadFilesAccept =
    Object.values(ReceiptImageFileTypeAvailable).join(', ') || '.jpg'

  const refFormik = useRef(null)
  const justificationRef = useRef(null)

  const [saving, setSaving] = useState<boolean>(false)
  const [previewImage, setPreviewImage] = useState<string>()

  const [barCharts, setBarCharts] = useState<BudgetChartBudgets[]>([])
  const [allocations, setAllocations] = useState<BudgetAllocationsCheck>({
    active: false,
    budgets: [],
    organization_id: -1,
    policy_rule_id: -1,
    tickets: []
  })

  const { t } = useT()
  const dispatch = useDispatch()

  const policyRule = useReasonPolicyRule(request.request_user_data[0].reason_id)
  const isToodoPayTicket = receiptEdit?.is_toodopay || false

  const userList = useMemo<UserOption[]>(
    () =>
      request.request_user_data
        .filter(item => item.traveler.user_visible)
        .map(item => ({
          gender: item.traveler.gender,
          label: item.traveler.name,
          value: `${item.traveler.user_id}`
        })),
    [request]
  )

  useEffect(() => {
    if (receipts.saveReceiptLoading !== saving) {
      setSaving(receipts.saveReceiptLoading)

      if (!receipts.saveReceiptLoading && !receipts.saveReceiptError) {
        message.success(t('common.push-notifications.receipt-saved-successful'))
        toggle()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts.saveReceiptLoading])

  const initialValues = useMemo(() => {
    if (receiptEdit) {
      return {
        ...receiptEdit,
        request_ticket_receipts: receiptEdit.request_ticket_receipts.map(
          item => ({
            ...item,
            amount: receiptEdit.is_toodopay || !item.quantity ? item.amount : 0,
            currency_id: item.currency_id.toString(),
            gas_value: item.gas_value || 0,
            payment_resource_id: item.payment_resource_id.toString(),
            reference_id: item.reference_id.toString(),
            service_id: item.service_id.toString()
          })
        )
      }
    }

    const defaultValues = getInitialValues()
    defaultValues.request_id = request.request_id
    defaultValues.request_ticket_receipts[0].date = moment
      .unix(request.requested_on)
      .set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
      .unix()

    if (user.reference && user.reference_id !== '') {
      defaultValues.request_ticket_receipts[0].reference_id = user.reference_id
      defaultValues.request_ticket_receipts[0].reference = user.reference
    }

    defaultValues.request_ticket_user_data = [
      {
        included: true,
        owner: true,
        request_ticket_id: null as Id,
        request_ticket_user_data_id: null as Id,
        traveler: {
          gender: userList[0]?.gender || Gender.X,
          name: userList[0]?.label || '',
          user_id: userList[0]?.value || ''
        },
        user_id: userList[0]?.value || ''
      }
    ]

    return defaultValues
  }, [receiptEdit, request.request_id, request.requested_on, user, userList])

  function findService(service_id: Id): Service {
    return resources.services.list.find(
      item => +item.service_id === +service_id
    )
  }

  function handleRenderAmount(formikProps: any): JSX.Element {
    const foundService = findService(
      formikProps.values.request_ticket_receipts[0].service_id
    )

    if (foundService && foundService.standard_type !== 'KM') {
      return (
        <Col span={6}>
          <InputCurrencyFreeDecimals
            dataTestId="receipt-amount"
            disabled={isToodoPayTicket}
            formikProps={formikProps}
            label="web.services.advances.form.amount.title"
            name="request_ticket_receipts.0.amount"
            placeholder="web.services.advances.form.amount.placeholder"
          />
        </Col>
      )
    }
    if (isTenant284264114255) {
      return (
        <Col span={6}>
          <InputCurrencyFreeDecimals
            dataTestId="receipt-amount"
            formikProps={formikProps}
            label="web.receipt.gas_value"
            name="request_ticket_receipts.0.gas_value"
          />
        </Col>
      )
    }
    return <div />
  }

  function handleRenderExchangeQuantity(formikProps: any): JSX.Element {
    const foundService = findService(
      formikProps.values.request_ticket_receipts[0].service_id
    )

    if (foundService) {
      const foundCurrency = resources.currencies.list.find(
        item =>
          +item.currency_id ===
          +formikProps.values.request_ticket_receipts[0].currency_id
      )

      return (
        <Col span={6}>
          {foundService.service_type === 'QUANTITY' && (
            <InputNumber
              dataTestId="receipt-quantity"
              formikProps={formikProps}
              label="web.receipt.form.quantity.title"
              min={0}
              name="request_ticket_receipts.0.quantity"
            />
          )}
          {foundService.service_type === 'VALUE' && (
            <InputCurrencyFreeDecimals
              dataTestId="receipt-exchange-rate"
              disabled={foundCurrency?.code === 'BRL'}
              formikProps={formikProps}
              label="web.services.advances.form.exchange-rate.title"
              name="request_ticket_receipts.0.exchange_rate"
              placeholder="web.services.advances.form.exchange-rate.placeholder"
            />
          )}
        </Col>
      )
    }
    return <div />
  }

  useEffect(() => {
    if (receipts.policyViolated) {
      if (justificationRef.current) {
        justificationRef.current.focus()
        refFormik.current.setFieldError(
          'request_ticket_receipts.0.justification',
          'common.required'
        )
      }
    }
  }, [receipts.policyViolated])

  useEffect(() => {
    if (!policyRule?.enable_budget) {
      return
    }

    const budgets =
      request.request_standard_assignments.request_standard_sharings.reduce(
        requestStandardSharingsReduce,
        []
      )

    const { organization_id } = request
    const { policy_rule_id } = policyRule
    const budget_allocations: BudgetAllocationsCheck = {
      active: true,
      budgets,
      organization_id,
      policy_rule_id,
      tickets: []
    }

    setAllocations(() => budget_allocations)
  }, [policyRule, request])

  useEffect(() => {
    let isSubscribed = true

    if (allocations.policy_rule_id <= -1 || !visible) {
      return () => {
        isSubscribed = false
      }
    }

    getBudgetAllocationsCheck(allocations).then(response => {
      if (isSubscribed) {
        setBarCharts(response)
      }
    })

    return () => {
      isSubscribed = false
    }
  }, [allocations, visible])

  useEffect(() => {
    if (!visible && receipts.saveReceiptError) {
      dispatch(saveReceiptFailure(null))
    }
  }, [dispatch, receipts.saveReceiptError, visible])

  function disabledDate(current: moment.Moment) {
    if (
      isTenant334081949181(+user.tenant_id) ||
      !current ||
      !policyRule?.policy_request_general_rule?.active ||
      !policyRule?.policy_request_general_rule?.minimum_days_limit
    ) {
      return false
    }

    return (
      current <
      moment
        .unix(request.requested_on)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    )
  }

  return (
    <Modal
      wrapProps={{ 'data-test-id': 'receipt-modal' }}
      cancelButtonProps={{ disabled: receipts.saveReceiptLoading }}
      cancelText="common.actions.cancel"
      closable={!receipts.saveReceiptLoading}
      destroyOnClose
      maskClosable={!receipts.saveReceiptLoading}
      okButtonProps={{ loading: receipts.saveReceiptLoading }}
      okText="common.actions.save"
      onCancel={() => toggle()}
      onOk={() => refFormik.current.submitForm()}
      title={
        receiptEdit
          ? stringify('web.receipt.form.title-edit', {
              number: receiptEdit.request_ticket_number ?? ''
            })
          : 'web.receipt.form.title'
      }
      visible={visible}
      width="55rem"
    >
      <Formik
        initialValues={initialValues}
        onSubmit={values => {
          if (
            isTenant284264114255 &&
            values.request_ticket_receipts[0].service?.standard_type === 'KM'
          ) {
            values.request_ticket_receipts[0].amount =
              Math.floor(
                (values.request_ticket_receipts[0].quantity / 10) *
                  values.request_ticket_receipts[0].gas_value *
                  100
              ) / 100
          }
          dispatch(saveReceipt({ ...values }))
        }}
        ref={refFormik}
        render={formikProps => {
          const images =
            formikProps.values.request_ticket_receipts[0].request_ticket_receipt_images.filter(
              item => item.active
            )
          const hasImageError =
            formikProps.errors?.request_ticket_receipts?.[0]
              ?.request_ticket_receipt_images ?? null

          return (
            <Form onSubmit={formikProps.handleSubmit}>
              <Row gutter={24}>
                <Col span={9}>
                  <Select
                    autoFocus
                    dataTestId="owner-list"
                    disabled={isToodoPayTicket}
                    formikProps={formikProps}
                    label="web.receipt.form.owner"
                    name="request_ticket_user_data.0.traveler.user_id"
                    onChange={(value: SelectValue) => {
                      formikProps.setFieldValue('request_ticket_user_data', [
                        {
                          included: true,
                          owner: true,
                          traveler: { user_id: value },
                          user_id: value
                        }
                      ])
                    }}
                    options={userList}
                  />
                </Col>
                <Col span={8}>
                  <SelectResource
                    formikProps={formikProps}
                    keyLabel="name"
                    keyValue="service_id"
                    label="web.receipt.form.service.title"
                    name="request_ticket_receipts.0.service_id"
                    onChange={(value: SelectValue) => {
                      const foundService = findService(`${value}`)

                      if (foundService?.service_type === 'QUANTITY') {
                        formikProps.setFieldValue(
                          'request_ticket_receipts.0.exchange_rate',
                          1
                        )
                        formikProps.setFieldValue(
                          'request_ticket_receipts.0.amount',
                          0
                        )
                      }

                      if (foundService?.service_type === 'VALUE') {
                        formikProps.setFieldValue(
                          'request_ticket_receipts.0.quantity',
                          0
                        )
                        formikProps.setFieldValue(
                          'request_ticket_receipts.0.quantity',
                          0
                        )
                      }
                    }}
                    organization_id={request.organization_id}
                    placeholder="web.receipt.form.service.placeholder"
                    resource="services"
                  />
                </Col>
                <Col span={7}>
                  <DatePicker
                    disabled={isToodoPayTicket}
                    disabledDate={disabledDate}
                    formikProps={formikProps}
                    label="web.receipt.form.date.title"
                    name="request_ticket_receipts.0.date"
                    placeholder="web.receipt.form.date.placeholder"
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <SelectResource
                    formikProps={formikProps}
                    keyLabel="name"
                    keyValue="reference_id"
                    label="web.receipt.form.reference.title"
                    name="request_ticket_receipts.0.reference_id"
                    organization_id={request.organization_id}
                    placeholder="web.receipt.form.reference.placeholder"
                    resource="references"
                  />
                </Col>
                <Col span={6}>
                  <SelectResource
                    formikProps={formikProps}
                    keyLabel="code"
                    keyValue="currency_id"
                    label="web.services.advances.form.currency.title"
                    name="request_ticket_receipts.0.currency_id"
                    organization_id={request.organization_id}
                    placeholder="web.services.advances.form.currency.placeholder"
                    resource="currencies"
                  />
                </Col>
                {handleRenderAmount(formikProps)}
                {handleRenderExchangeQuantity(formikProps)}
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <SelectResource
                    disabled={isToodoPayTicket}
                    formikProps={formikProps}
                    keyLabel="name"
                    keyValue="payment_resource_id"
                    label="web.receipt.form.payment-resource.title"
                    name="request_ticket_receipts.0.payment_resource_id"
                    organization_id={request.organization_id}
                    placeholder="web.receipt.form.payment-resource.placeholder"
                    resource="payment_resources"
                  />
                  {t('web.table.hotel.refundable')}
                  {': '}
                  <Checkbox
                    disabled
                    checked={
                      formikProps.values.request_ticket_receipts?.[0]
                        ?.payment_resource?.refundable ?? false
                    }
                  />
                </Col>
                <Col span={6}>
                  <Input
                    data-test-id="receipt-invoice"
                    formikProps={formikProps}
                    label="web.receipt.form.invoice.title"
                    name="request_ticket_receipts.0.invoice"
                    placeholder="web.receipt.form.invoice.placeholder"
                    required={false}
                  />
                </Col>
                <Col span={12}>
                  <Input
                    data-test-id="receipt-justification"
                    formikProps={formikProps}
                    innerRef={justificationRef}
                    label="web.receipt.form.justification.title"
                    name="request_ticket_receipts.0.justification"
                    placeholder="web.receipt.form.justification.placeholder"
                    required={receipts.policyViolated}
                  />
                </Col>
              </Row>
              <Divider />
              <Row>
                <Col span={12}>
                  <CSS.Upload
                    accept={uploadFilesAccept}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    customRequest={() => {}}
                    fileList={images.map(item => ({
                      uid: (
                        item.request_ticket_receipt_image_id ||
                        item.tmp_id ||
                        0
                      ).toString(),
                      name: (
                        item.request_ticket_receipt_image_id ||
                        item.tmp_id ||
                        0
                      ).toString(),
                      size: 0,
                      url: item.image_url,
                      type: 'done'
                    }))}
                    listType="picture-card"
                    onChange={async ({ file }: UploadChangeParam) => {
                      if (!file) {
                        return
                      }

                      const isFileTypeAllowed = uploadFilesAccept.includes(
                        file.type
                      )

                      if (!isFileTypeAllowed && file.status !== 'removed') {
                        notification.warning({
                          duration: 10,
                          message: t({
                            message:
                              'common.receipt.warning.unsupported-format',
                            params: {
                              value: uploadFilesAccept
                                .replace(/(image|application)\//g, '')
                                .toUpperCase()
                            }
                          }),
                          placement: 'bottomRight'
                        })
                        return
                      }

                      if (file.status === 'removed') {
                        if (file.uid.indexOf('.') > -1) {
                          formikProps.setFieldValue(
                            'request_ticket_receipts.0.request_ticket_receipt_images',
                            formikProps.values.request_ticket_receipts[0].request_ticket_receipt_images.filter(
                              item => +item.tmp_id !== +file.uid
                            )
                          )
                          return
                        }

                        formikProps.setFieldValue(
                          'request_ticket_receipts.0.request_ticket_receipt_images',
                          formikProps.values.request_ticket_receipts[0].request_ticket_receipt_images.map(
                            item => ({
                              ...item,
                              active:
                                +item.request_ticket_receipt_image_id ===
                                +file.uid
                                  ? false
                                  : item.active
                            })
                          )
                        )
                        return
                      }

                      if (file.type === 'application/pdf') {
                        formikProps.setFieldValue(
                          'request_ticket_receipts.0.request_ticket_receipt_images',
                          [
                            ...formikProps.values.request_ticket_receipts[0]
                              .request_ticket_receipt_images,
                            {
                              active: true,
                              image_url: await getBase64(file.originFileObj),
                              request_ticket_receipt_image_id: '',
                              request_ticket_receipt_image_number: '',
                              tmp_id: Math.random()
                            }
                          ]
                        )
                      } else {
                        new Compressor(file.originFileObj, {
                          mimeType: 'image/jpeg',
                          checkOrientation: false,
                          maxWidth: 1080,
                          maxHeight: 1080,
                          quality: 0.6,
                          async success(result) {
                            formikProps.setFieldValue(
                              'request_ticket_receipts.0.request_ticket_receipt_images',
                              [
                                ...formikProps.values.request_ticket_receipts[0]
                                  .request_ticket_receipt_images,
                                {
                                  active: true,
                                  image_url: await getBase64(result),
                                  request_ticket_receipt_image_id: '',
                                  request_ticket_receipt_image_number: '',
                                  tmp_id: Math.random()
                                }
                              ]
                            )
                          },
                          error(err) {
                            notification.warning({
                              duration: 10,
                              message: t({
                                message: 'web.receipt.form.images.not-attached'
                              }),
                              placement: 'bottomRight'
                            })
                          }
                        })
                      }
                    }}
                    onPreview={file => setPreviewImage(file.url)}
                  >
                    {images.length < numberSizeForUpload && <AddImage />}
                  </CSS.Upload>
                  {hasImageError && (
                    <span style={{ paddingLeft: '1rem', color: '#f5222d' }}>
                      {t(hasImageError)}
                    </span>
                  )}
                </Col>
                <Col span={12}>
                  {policyRule?.enable_budget && (
                    <BudgetReceipt bars={barCharts} />
                  )}
                </Col>
              </Row>

              {receipts.saveReceiptError && (
                <CSS.Alert
                  message={receipts.saveReceiptError}
                  showIcon
                  type="error"
                />
              )}
            </Form>
          )
        }}
        validationSchema={getValidationSchema({
          findService,
          force_image: policyRule?.force_image,
          kmNeedJustification: isTenant559042084949(+user.tenant_id),
          useGasValue: isTenant284264114255
        })}
        validateOnBlur={false}
      />
      <ThumbnailPreview
        initPreview={previewImage}
        onCancel={() => setPreviewImage(null)}
      />
    </Modal>
  )
}
